import React, { useRef, useState, useContext } from 'react';
import { Box, Button, CircularProgress, Container, Grid, IconButton, MenuItem, Modal, Select, Stack, TextField, Typography } from "@mui/material";
import CloseIcon from '@mui/icons-material/Close';
import UploadOutlinedIcon from '@mui/icons-material/UploadOutlined';
import dashboardServices from '../../services/hoa-dashboard-api-service';
import datastore from '../../store/hoa-datastore';
import SuccessMessage from '../success-message/hoa-success-message';
import config from '../../hoa-config';
import { GlobalContext } from '../../context/hoa-global-context';
import LoadingComponent from '../loading-component/loading-component';


const DocumentUploadModal = (props: any) => {
  const { document, updateDocumentData} = useContext<any>(GlobalContext);
  const { uploadModalOpen, toggleUploadModal, fetchData } = props;
  const [hoaVendorID, setHoaVendorID] = useState("");
  const [externalID, setExternalID] = useState("");
  const [documentType, setDocumentType] = useState("");
  const [organizationId, setOrganizationId] = useState("");
  const [files, setFiles] = useState([]);
  const [isUploading, setIsUploading] = useState(false);
  const [success, setSuccess] = useState(false);
  const [message, setMessage] = useState('');
  const [errors, setErrors] = useState({
    hoaVendorID: false,
    externalID: false,
    documentType: false,
    organizationId: false,
    files: false,
  });

  const uploadButtonRef = useRef(null);

  const handleUploadButtonClick = () => {
    uploadButtonRef.current.click();
  };

  const handleFileChange = (event: any) => {
    const selectedFiles = Array.from(event.target.files);
    setFiles((prevFiles) => [...prevFiles, ...selectedFiles]);
    setErrors((prev) => ({ ...prev, files: false }));
  };

  const handleFileRemove = (index: any) => {
    setFiles((prevFiles) => prevFiles.filter((_, i) => i !== index));
  };

  const handleUpload = async () => {
  
    const newErrors = {
      hoaVendorID: !hoaVendorID,
      externalID: !externalID,
      documentType: !documentType,
      organizationId: !organizationId,
      files: files.length === 0,
    };
    setErrors(newErrors);
  
    if (Object.values(newErrors).some((error) => error)) {
      setMessage("Please fill out all fields and upload at least one valid file.");
      setSuccess(true);
      return;
    }
  
    setIsUploading(true);
    const formData = new FormData();
    files.forEach((file) => formData.append('files', file));
    formData.append('hoaVendorID', hoaVendorID);
    formData.append('externalID', externalID);
    formData.append('documentType', documentType);
    formData.append('organizationId', organizationId);
    formData.append('addedBy', 'Internal');
  
    try {
      const res = await dashboardServices.createDocuments(formData);
      if (res.status == 200) {
        let allData = JSON.parse(JSON.stringify(document.data));
        allData = [...res.data, ...allData];
        let updatedData = {
            ...document,
            fetchingFilteredData:true,
            data: allData,
        }
        updateDocumentData(updatedData)
        setSuccess(true);
        setMessage(`Document added successfully.`);
      } else {
          setSuccess(true);
          setMessage(`Something went wrong!`);
      }
      await handleDocumentLog(res);
      setMessage("Files uploaded successfully.");
      setSuccess(true); 
      handleClose();
    } catch (error) {
      console.error('An error occurred during file upload:', error); 
      setMessage("An error occurred during file upload.");  
    } finally {
      setIsUploading(false);
    }
  };  

  const handleDocumentLog = async (apiResponse: any) => {
    if (Array.isArray(apiResponse.data)) {
      await Promise.all(apiResponse.data.map(async (item: any) => {
        let logsData = {
          action: 'Uploaded & Pending',
          email: item,
          updatedBy: datastore.ownerInformation.email,
          documentId: item?.objectId 
        };
        await dashboardServices.createDocumentLog(logsData);
        console.log(item)
      }));
      
      setMessage("Files uploaded successfully.");
      setSuccess(true);
    } else {
      console.warn("Expected apiResponse.data to be an array.");
    }
  };

  const handleClose = () => {
    toggleUploadModal();
    setHoaVendorID("");
    setExternalID("");
    setDocumentType("");
    setOrganizationId("");
    setFiles([]);
    setErrors({
      hoaVendorID: false,
      externalID: false,
      documentType: false,
      organizationId: false,
      files: false,
    });
  };

  return (
    <>
      <LoadingComponent loading={isUploading} />
      <Modal open={uploadModalOpen}>
        <Container style={{
          background: "white",
          position: "absolute",
          top: "50%",
          left: "50%",
          transform: "translate(-50%, -50%)",
          width: "548px",
          maxHeight: "645px",
          overflow: "auto",
          borderRadius: "8px 8px 0px 0px",
          padding: "0px",
          overflowX: 'hidden'
        }}>
        <div style={{ background: "#EFF4FC", display: 'flex', justifyContent: 'space-between' }}>
          <div className="modal-title-container">
            <span className="text-3E3E3E-20px bold">{`Upload Document`}</span>
          </div>
          <IconButton style={{ padding: '24px', cursor: 'pointer' }} onClick={handleClose} disabled={isUploading}>
            <CloseIcon />
          </IconButton>
        </div>
        <Box display="flex" flexDirection="column" style={{ padding: '0px 20px' }}>
          <Grid container spacing={2}>
            <Grid item xs={12} sm={6}>
              <TextField
                style={{ padding: "10px 0px", width: "250px", marginRight: "17px", marginTop: "17px" }}
                InputLabelProps={{ style: { fontSize: "14px" } }}
                label="HOA Vendor ID"
                variant="standard"
                value={hoaVendorID}
                onChange={(e) => {
                  setHoaVendorID(e.target.value);
                  setErrors((prev) => ({ ...prev, hoaVendorID: false }));
                }}
                error={errors.hoaVendorID}
                helperText={errors.hoaVendorID ? "HOA Vendor ID is required" : ""}
                required
                fullWidth
              />
            </Grid>
            <Grid item xs={12} sm={6}>
              <TextField
                style={{ padding: "10px 0px", width: "250px", marginRight: "17px", marginTop: "17px" }}
                InputLabelProps={{ style: { fontSize: "14px" } }}
                label="External ID"
                variant="standard"
                value={externalID}
                onChange={(e) => {
                  setExternalID(e.target.value);
                  setErrors((prev) => ({ ...prev, externalID: false }));
                }}
                error={errors.externalID}
                helperText={errors.externalID ? "External ID is required" : ""}
                required
                fullWidth
              />
            </Grid>
            <Grid item xs={12} sm={6}>
              <Select
                style={{ padding: "10px 0px", width: "250px", marginRight: "17px", marginTop: "25px", fontSize: "14px" }}
                variant="standard"
                value={documentType}
                onChange={(e) => {
                  setDocumentType(e.target.value);
                  setErrors((prev) => ({ ...prev, documentType: false }));
                }}
                displayEmpty
                error={errors.documentType}
                required
                fullWidth
              >
                <MenuItem value="" disabled>Select Document Type *</MenuItem>
                <MenuItem value="CCNR">CCNR</MenuItem>
                <MenuItem value="Violations">Violations</MenuItem>
                <MenuItem value="CCR_HOA">CCR_HOA</MenuItem>
                <MenuItem value="HOA">HOA</MenuItem>
                <MenuItem value="ByLaws">ByLaws</MenuItem>
                <MenuItem value="Others">Others</MenuItem>
              </Select>
              {errors.documentType && (
                <Typography color="error" variant="caption">Document Type is required</Typography>
              )}
            </Grid>
            <Grid item xs={12} sm={6}>
            <Select
                style={{ padding: "10px 0px", width: "250px", marginRight: "17px", marginTop: "25px", fontSize: "14px" }}
                variant="standard"
                value={organizationId}
                onChange={(e) => {
                setOrganizationId(e.target.value);
                setErrors((prev) => ({ ...prev, organizationId: false }));
              }}
              displayEmpty
              error={errors.documentType}
              required
              fullWidth
              >
            <MenuItem value="" disabled>Select Organization *</MenuItem>
            {config.hoaOrganizations.map((org: any) => (
            <MenuItem key={org.id} value={org.id}>
              {org.name}
            </MenuItem>
              ))}
            </Select>
            </Grid>
          </Grid>
          <Box mt={"20px"} mb={"20px"}>
            <Typography className="text-14px-5B5B5B">Source Document</Typography>
            <Stack flexDirection={"column"}>
              <Button
                sx={{ width: "180px", marginTop: '20px' }}
                startIcon={<UploadOutlinedIcon color="primary" />}
                variant="outlined"
                onClick={handleUploadButtonClick}
              >
                Upload Document
              </Button>
              <input
                ref={uploadButtonRef}
                type="file"
                multiple
                hidden
                accept="application/pdf,application/vnd.ms-excel,.doc,.docx,application/msword,application/vnd.openxmlformats-officedocument.wordprocessingml.document"
                onChange={handleFileChange}
              />

              {files.length > 0 && (
                <>
                  <Typography className="helperText" style={{ paddingTop: '12px', paddingBottom: '12px' }}>Selected Files:</Typography>
                  {files.map((file, index) => (
                    <Box key={index} display="flex" alignItems="center" justifyContent="space-between" padding="5px 10px" style={{ border: '1px dashed #5B5B5B', borderRadius: '5px', marginBottom: '4px'}}>
                      <Typography variant="body2" style={{ color: '#5B5B5B' }}>{file.name} ({(file.size / 1024).toFixed(2)} KB)</Typography>
                      <IconButton size="small" onClick={() => handleFileRemove(index)}>
                        <CloseIcon fontSize="small" />
                      </IconButton>
                    </Box>
                  ))}
                </>
              )}
            </Stack>
          </Box>

          {/* Upload and Cancel buttons */}
          <Box display="flex" justifyContent="flex-end" gap="10px" padding="20px 0">
            <Button variant="contained" disabled={isUploading} style={{ backgroundColor: "#E7EFFC", color: "#3576E5", width: "110px", height: "40px" }} onClick={handleClose}>
              Cancel
            </Button>
            <Button variant="contained" color="primary" onClick={handleUpload} disabled={isUploading} style={{ width: "110px", height: "40px" }} startIcon={isUploading && <CircularProgress size={20} />}>
              {isUploading ? "Uploading" : "Upload"}
            </Button>
          </Box>
          </Box>
        </Container>
      </Modal>
      <SuccessMessage success={success} setSuccess={setSuccess} message={message} />
    </>
  );
};

export default DocumentUploadModal;
